import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Video, RefreshCw, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { DOCTOR } from '@/constants/testIds';

const isFresh = (iso) => {
  if (!iso) return false;
  try {
    const t = new Date(iso).getTime();
    return Date.now() - t < 5 * 60 * 1000; // booked in last 5 minutes
  } catch { return false; }
};

const ApptCard = ({ a, fresh }) => (
  <div
    data-testid={DOCTOR.apptCard(a.appointment_id)}
    className={`relative border bg-white p-6 hover:shadow-[0_8px_32px_rgba(0,0,0,0.04)] transition-shadow ${fresh ? 'border-emerald-400 ring-1 ring-emerald-100' : 'border-sans_line'}`}
  >
    {fresh && (
      <span className="absolute -top-2 left-6 bg-emerald-500 text-white text-[10px] uppercase tracking-[0.2em] px-2 py-1">
        New
      </span>
    )}
    <Link to={`/doctor/appointments/${a.appointment_id}`} className="block">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="sans-label mb-2 flex items-center gap-2 flex-wrap">
            <span>
              {new Date(a.slot_datetime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} · {new Date(a.slot_datetime).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
            </span>
            {a.type === 'diet' ? (
              <span className="text-[10px] uppercase tracking-[0.2em] border border-emerald-700 text-emerald-800 px-2 py-0.5">Diet · ₹2,900</span>
            ) : (
              <span className="text-[10px] uppercase tracking-[0.2em] border border-sans_line text-sans_mute px-2 py-0.5">Consult · ₹1,000</span>
            )}
          </div>
          <div className="font-serif text-xl text-sans_ink">{a.user_name}</div>
          <div className="text-xs text-sans_mute mt-1">{a.user_email}</div>
          {a.user_phone && <div className="text-xs text-sans_mute mt-1">{a.user_phone}</div>}
          {a.reason && <div className="mt-3 text-sm text-sans_mute leading-relaxed max-w-md">{a.reason}</div>}
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-sans_mute">{a.status}</div>
      </div>
    </Link>
    {a.meet_link && (
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <a
          href={a.meet_link}
          target="_blank"
          rel="noreferrer noopener"
          data-testid={`join-meet-${a.appointment_id}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] bg-sans_ink text-sans_bone px-4 py-2.5 hover:opacity-90 transition-opacity"
        >
          <Video className="w-3.5 h-3.5" strokeWidth={1.75} /> Join meeting
        </a>
        <button
          type="button"
          data-testid={`copy-meet-${a.appointment_id}`}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            navigator.clipboard.writeText(a.meet_link).then(
              () => toast.success('Meet link copied — paste in SMS or WhatsApp'),
              () => toast.error('Copy failed — please copy manually'),
            );
          }}
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] border border-sans_line text-sans_ink px-4 py-2.5 hover:bg-sans_line/40 transition-colors"
        >
          <Copy className="w-3.5 h-3.5" strokeWidth={1.75} /> Copy link
        </button>
      </div>
    )}
  </div>
);

const DoctorHome = () => {
  const [data, setData] = useState({ today: [], upcoming: [], past: [] });
  const [refreshing, setRefreshing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const seenIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data: d } = await api.get('/doctor/appointments');
      // Detect new appointments since last poll
      const allIds = [...(d.today || []), ...(d.upcoming || [])].map((a) => a.appointment_id);
      if (!firstLoadRef.current) {
        const previouslySeen = seenIdsRef.current;
        const newOnes = allIds.filter((id) => !previouslySeen.has(id));
        if (newOnes.length > 0) {
          toast.success(`${newOnes.length} new booking${newOnes.length > 1 ? 's' : ''}`, {
            description: 'Refreshed just now',
          });
        }
      }
      seenIdsRef.current = new Set(allIds);
      firstLoadRef.current = false;
      setData(d);
      setLastSync(new Date());
      // Update tab title with today count
      const n = (d.today || []).length;
      document.title = n > 0 ? `(${n}) Doctor · Saans Ayurved` : 'Doctor · Saans Ayurved';
    } catch {
      // silent — next tick will retry
    } finally {
      if (!silent) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(() => load(true), 20000); // poll every 20 s
    // Re-fetch immediately when the tab regains focus
    const onFocus = () => load(true);
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); document.title = 'Saans Ayurved'; };
  }, [load]);

  return (
    <div>
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl text-sans_ink tracking-tight">Today's schedule</h1>
          <p className="text-sans_mute mt-2">Your appointments, patients, and prescriptions.</p>
        </div>
        <button
          type="button"
          onClick={() => load()}
          data-testid="doctor-refresh-btn"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] border border-sans_line px-4 py-2.5 text-sans_ink hover:bg-sans_ink hover:text-sans_bone transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} strokeWidth={1.75} />
          {refreshing ? 'Syncing…' : 'Refresh'}
        </button>
      </div>
      {lastSync && (
        <div className="mt-2 text-[11px] text-sans_mute">
          Auto-refreshing every 20s · Last synced {lastSync.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>
      )}

      <div className="mt-12">
        <div className="sans-label mb-4">Today · {data.today.length}</div>
        {data.today.length === 0 && <div className="text-sm text-sans_mute">No appointments scheduled today.</div>}
        <div className="grid md:grid-cols-2 gap-4">{data.today.map((a) => <ApptCard key={a.appointment_id} a={a} fresh={isFresh(a.created_at)} />)}</div>
      </div>

      <div className="mt-14">
        <div className="sans-label mb-4">Upcoming · {data.upcoming.length}</div>
        {data.upcoming.length === 0 && <div className="text-sm text-sans_mute">No upcoming appointments.</div>}
        <div className="grid md:grid-cols-2 gap-4">{data.upcoming.map((a) => <ApptCard key={a.appointment_id} a={a} fresh={isFresh(a.created_at)} />)}</div>
      </div>

      <div className="mt-14">
        <div className="sans-label mb-4">Past · {data.past.length}</div>
        <div className="grid md:grid-cols-2 gap-4">{data.past.slice(0, 10).map((a) => <ApptCard key={a.appointment_id} a={a} fresh={false} />)}</div>
      </div>
    </div>
  );
};

export default DoctorHome;
